import { FC, ReactNode } from "react";
import classNames from "classnames";
import { Link } from "react-router";

import Skeleton from "../Skeleton/Skeleton";
import { OverviewStatsCardItem, OverviewStatsProps } from "./types";
import { mapModelIcon } from "../../types/models";
import type { ModelIcons } from "../../types/models";

import styles from "./OverviewStats.module.css";



const OverviewStats: FC<OverviewStatsProps> = ({
  className,
  visibility,
  sentiment,
  ranking,
  isLoading,
  topModel,
  topModelLoading,
  topModelLoadComplete = false,
  onTopModelClick,
  topSource,
  topSourceLoading = false,
  linkToBrands,
}) => {
  const renderValue = (
    value: number | null | undefined,
  ): ReactNode => {
    if (isLoading) {
      return <Skeleton height={52} width={75} variant="text" />;
    }
    if (value == null) {
      return "—";
    }
    return String(value);
  };

  const renderTopModelIcon = (): ReactNode => {
    const showSkeleton = topModelLoading || (!topModelLoadComplete && !topModel);
    if (showSkeleton) {
      return <Skeleton height={48} width={48} variant="circular" />;
    }
    if (!topModel) return null;
    const iconKey = topModel.name.toLowerCase() as ModelIcons;
    const IconComponent = mapModelIcon(iconKey);
    return <IconComponent />;
  };

  const renderTopSourceIcon = (): ReactNode => {
    if (topSourceLoading) {
      return <Skeleton height={48} width={48} variant="circular" />;
    }
    if (!topSource) return null;
    if (topSource.logoUrl) {
      return (
        <img
          src={topSource.logoUrl}
          alt=""
          className={styles["overview-stats__top-source-img"]}
        />
      );
    }
    return (
      <span className={styles["overview-stats__top-source-fallback"]}>
        {topSource.name.slice(0, 2).toUpperCase()}
      </span>
    );
  };


  const metricCards: OverviewStatsCardItem[] = [
    {
      id: "visibility",
      value: renderValue(visibility),
      description: "Visibility",
      href: linkToBrands,
    },
    {
      id: "sentiment",
      value: renderValue(sentiment),
      description: "Sentiment",
      href: linkToBrands,
    },
    {
      id: "ranking",
      value: renderValue(ranking),
      description: "Ranking",
      href: linkToBrands,
    },
  ];

  const topModelCard: OverviewStatsCardItem = {
    id: "top-ai-model",
    value:
      topModel != null
        ? undefined
        : topModelLoadComplete && !topModelLoading
          ? "—"
          : undefined,
    description: "Top AI model",
    icon: renderTopModelIcon(),
    onClick: topModel && onTopModelClick ? () => onTopModelClick(topModel.model_id) : undefined,
  };

  const topSourceCard: OverviewStatsCardItem = {
    id: "top-source",
    value:
      topSource != null
        ? undefined
        : !topSourceLoading
          ? "—"
          : undefined,
    description: "Top source",
    icon: renderTopSourceIcon(),
    href: topSource?.detailHref,
  };

  const staticCards: OverviewStatsCardItem[] = [topSourceCard, topModelCard];
  const cards = [...metricCards, ...staticCards];

  return (
    <div className={classNames(styles["overview-stats"], className)}>
      {cards.map((card) => {
        const isLink = Boolean(card.href);
        const isButton = Boolean(card.onClick) && !isLink;
        const isClickable = isLink || isButton;

        const cardContent = (
          <>
            {card.icon != null ? (
              <div className={styles["overview-stats__value-icon"]}>
                {card.icon}
              </div>
            ) : null}
            {card.value != null ? (
              <div className={styles["overview-stats__value"]}>{card.value}</div>
            ) : null}
            <p className={styles["overview-stats__description"]}>
              {card.description}
            </p>
          </>
        );

        const cardClassName = classNames(
          styles["overview-stats__card"],
          isClickable && styles["overview-stats__card_is-clickable"]
        );

        if (isLink && card.href) {
          return (
            <Link
              key={card.id}
              to={card.href}
              className={cardClassName}
            >
              {cardContent}
            </Link>
          );
        }
        if (isButton) {
          return (
            <button
              key={card.id}
              type="button"
              className={cardClassName}
              onClick={card.onClick}
            >
              {cardContent}
            </button>
          );
        }
        return (
          <div key={card.id} className={cardClassName}>
            {cardContent}
          </div>
        );
      })}
    </div>
  );
};

export default OverviewStats;
